<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { computed } from 'vue'
import { SvgIcon } from '@progress/kendo-vue-common'
import { checkOutlineIcon } from '@progress/kendo-svg-icons'

import type { IAoeCart, IHostRewardSummaryItem } from '@/types/CartTypes'
import { HostRewardSummaryKind } from '@/constants/DiscountAwardType'
import { BuildHostRewardsSummary } from '@/services/HostRewardsSummaryService'
import { FormatCurrency } from '@/services/currencyFormatter'

const { t } = useI18n()

const props = defineProps<{
  cart: IAoeCart | null
}>()

// Host rewards only apply to the Host's cart - never render for guest or
// consultant carts, regardless of where the component is placed.
const isHostCart = computed<boolean>(
  () =>
    props.cart != null &&
    props.cart.aoeCartIdToken != '' &&
    props.cart.isHostCart === true,
)

const rewards = computed<IHostRewardSummaryItem[]>(() =>
  BuildHostRewardsSummary(props.cart),
)

function FormatValue(reward: IHostRewardSummaryItem, value: number | null) {
  if (value == null) return ''
  return reward.kind === HostRewardSummaryKind.CURRENCY
    ? FormatCurrency(value, null)
    : String(value)
}
</script>

<template>
  <div
    v-if="isHostCart"
    class="aoe-host-rewards-summary"
    data-testid="host-rewards-summary"
  >
    <div class="row party-order-entry-section-header-row">
      <div
        class="col-12 party-order-entry-section-header-div host-rewards-summary-header"
      >
        {{ t('hostRewardsSummary.SectionHeader') }}
      </div>
    </div>

    <div
      v-if="rewards.length === 0"
      class="row mt-3 host-rewards-summary-empty"
      data-testid="host-rewards-summary-empty"
    >
      <div class="col-12">{{ t('hostRewardsSummary.NoRewardsEarned') }}</div>
    </div>

    <ul v-else class="host-rewards-summary-list">
      <li
        v-for="reward in rewards"
        :key="reward.poeDiscPlanLevelDiscId"
        class="host-rewards-summary-item"
        :data-testid="`host-reward-${reward.poeDiscPlanLevelDiscId}`"
      >
        <SvgIcon class="host-rewards-summary-icon" :icon="checkOutlineIcon" />
        <div class="host-rewards-summary-text">
          <div class="host-rewards-summary-label">{{ t(reward.labelKey) }}</div>
          <div v-if="reward.detail" class="host-rewards-summary-detail">
            {{ reward.detail }}
          </div>
        </div>
        <div class="host-rewards-summary-values">
          <template v-if="reward.kind === HostRewardSummaryKind.FLAG">
            <span class="host-rewards-summary-value">
              {{ t('hostRewardsSummary.Earned') }}
            </span>
          </template>
          <template v-else>
            <span class="host-rewards-summary-value">
              <span class="host-rewards-summary-value-label">
                {{ t('hostRewardsSummary.EarnedLabel') }}
              </span>
              {{ FormatValue(reward, reward.earned) }}
            </span>
            <span
              class="host-rewards-summary-value"
              :class="{ 'host-rewards-summary-value-open': (reward.remaining ?? 0) > 0 }"
            >
              <span class="host-rewards-summary-value-label">
                {{ t('hostRewardsSummary.RemainingLabel') }}
              </span>
              {{ FormatValue(reward, reward.remaining) }}
            </span>
          </template>
        </div>
      </li>
    </ul>
  </div>
</template>

<style lang="scss">
.aoe-host-rewards-summary {
  .host-rewards-summary-empty {
    @include typography-body;
  }

  .host-rewards-summary-list {
    list-style: none;
    margin: $spacing-3 0 0;
    padding: 0;
  }

  .host-rewards-summary-item {
    display: flex;
    align-items: center;
    gap: $spacing-3;
    padding: $spacing-3 0;
    border-bottom: 1px solid var(--color-border);

    &:last-child {
      border-bottom: 0;
    }
  }

  .host-rewards-summary-icon {
    flex: 0 0 auto;
    width: 24px;
    height: 24px;
    color: var(--standardColor);
  }

  .host-rewards-summary-text {
    flex: 1 1 auto;
    min-width: 0;
  }

  .host-rewards-summary-label {
    @include typography-body;
    @include font-weight-semibold;
  }

  .host-rewards-summary-detail {
    @include typography-small;
  }

  .host-rewards-summary-values {
    flex: 0 0 auto;
    display: flex;
    gap: $spacing-4;
    text-align: right;
    @include typography-body;
  }

  .host-rewards-summary-value-label {
    @include typography-small;
  }

  .host-rewards-summary-value-open {
    @include font-weight-semibold;
    color: var(--standardColor);
  }

  @media screen and (max-width: 768px) {
    .host-rewards-summary-item {
      flex-wrap: wrap;
    }

    .host-rewards-summary-values {
      flex-basis: 100%;
      justify-content: flex-start;
      padding-left: calc(24px + #{$spacing-3});
      text-align: left;
    }
  }
}
</style>
